import { useEffect, useState } from 'react';

/**
 * 是否走移动端紧凑布局。
 *
 * 判断口径（任一命中即视为 mobile）：
 *   1) URL query 含 ?mobile=1：手动强制（开发联调 / 灰度兜底）
 *      ?mobile=0：手动强制走 PC（也是兜底）
 *   2) userAgent 含真实移动设备关键字（iPhone / iPad / Android / Mobile
 *      / webOS / BlackBerry 等）：钉钉手机端 webview / Safari / Chrome
 *      Android / 微信 / 浏览器 webview 都会命中
 *   3) window.innerWidth < 768：PC 浏览器小窗兜底
 *
 * 不再因为 UA 含 "DingTalk" 就强制 mobile —— 钉钉 PC 客户端的 UA 也含
 * "DingTalk"（用户 2026-04-30 22:18 截图反馈：电脑钉钉里看到了卡片版
 * 不是表格），但用户的诉求是「电脑钉钉 = PC 体验」，只有「真正的手机」
 * 才走 mobile 紧凑布局。
 *
 * 钉钉 PC 客户端 UA 典型：
 *   "...DingTalk(2.x.x) Mozilla/5.0 ... Macintosh ..." → 不含 iPhone/
 *   Android/Mobile 关键字，不命中 → PC
 * 钉钉手机端 UA 典型：
 *   "Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 ...) ... DingTalk(...)" →
 *   含 iPhone 关键字，命中 → mobile
 *
 * 派生效果（在 main.tsx 里）：
 *   - body.classList toggle "tg-mobile"，方便 CSS 用 `.tg-mobile` 选择
 *     器作用域命中（不依赖 media query）
 *   - antd ConfigProvider 切到 compactAlgorithm
 *
 * 同时被 Layout.tsx 用于决定 Sider 显隐 / 汉堡按钮。
 */
export function detectMobile(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mobile') === '1') return true;
    if (params.get('mobile') === '0') return false;
  } catch {
    // location 不可访问时忽略
  }

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  // 真实移动设备：钉钉手机端 / Safari / Chrome Android / 微信等
  if (/iPhone|iPad|iPod|Android|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }

  // PC 浏览器（含钉钉 PC 客户端）：仅在窗口确实窄时兜底走 mobile
  return window.innerWidth < 768;
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => detectMobile());

  useEffect(() => {
    const apply = () => setIsMobile(detectMobile());
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);

  return isMobile;
}
